import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  AlertTriangle,
  ClipboardSignature,
  FileText,
  ShieldCheck,
  Download,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import { binder, studies, ApiError, type BinderFolderRow, type StudyRow, type StudySiteBinderRow } from '@/api/client';
import { studyName } from '@/lib/studyLabel';
import { BinderFolderTree } from './components/BinderFolderTree';
import { DocumentSlotList } from './components/DocumentSlotList';
import { DocumentViewerModal } from '@/components/binder/DocumentViewerModal';
import { BulkExportDialog } from '@/components/binder/BulkExportDialog';
import { ExternalGrantsTab } from './external/ExternalGrantsTab';
import { RaiseFolderQueryDialog } from './components/RaiseFolderQueryDialog';
import { useEisfPermissions } from '@/hooks/useEisfPermissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { BlindedNotice } from '@/components/layout/BlindedNotice';
import { BlindingScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { binderScope, binderScopeSuffix, sortBindersPrimaryFirst } from '@/lib/binderScope';
import { cn } from '@/lib/cn';

const QUERY_RAISE_ROLES = ['monitor', 'auditor'] as const;

function folderDisplayName(f: BinderFolderRow, idx?: number): string {
  return (
    f.display_name_override ||
    f.subartifact_label ||
    f.artifact_type ||
    (idx !== undefined ? `Document ${idx + 1}` : 'Unnamed')
  );
}

export default function BinderDetailPage() {
  const { binderId: binderRef = '' } = useParams<{ binderId: string }>();
  const navigate = useNavigate();

  // Load the full binder list so the study selector dropdown can show all options
  // for this site and let the user switch without going back to the list page.
  const listQ = useQuery({
    queryKey: ['binder', 'list'],
    queryFn: binder.list,
    staleTime: 60_000,
  });
  const allBinders: StudySiteBinderRow[] = useMemo(
    () => sortBindersPrimaryFirst(listQ.data?.binders ?? []),
    [listQ.data],
  );

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: studies.list,
    staleTime: 5 * 60_000,
  });
  // Study NAME in the selector, not the protocol number (client 2026-10-14,
  // "study name everywhere"; this dropdown was missed in that sweep).
  const studyById = useMemo(() => {
    const m = new Map<string, StudyRow>();
    for (const s of studiesQ.data?.rows ?? []) m.set(s.study_id, s);
    return m;
  }, [studiesQ.data]);
  const binderOptionLabel = (b: StudySiteBinderRow) => {
    const study = studyById.get(b.study_id);
    const ref = b.reference_number || b.binder_id;
    return (study ? `${studyName(study)} (${ref})` : b.binder_name || ref) + binderScopeSuffix(b);
  };

  const q = useQuery({
    queryKey: ['binder', binderRef],
    queryFn: () => binder.get(binderRef),
    enabled: !!binderRef,
  });

  const binderId = q.data?.binder?.binder_id ?? '';
  const folders = q.data?.folders ?? [];
  const folderCompleteness = q.data?.folder_completeness ?? {};
  const binderBlinded = q.data?.blinded === true;
  const binderBlindedRoleRef = q.data?.blinded_role_master_ref ?? null;

  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => a.sort_order - b.sort_order),
    [folders],
  );

  const qc = useQueryClient();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);
  const [viewerVersionId, setViewerVersionId] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [externalOpen, setExternalOpen] = useState(false);
  const [treeSearch, setTreeSearch] = useState('');
  const { can } = useEisfPermissions();

  // Phase 4 base FE (AC 4.1) — folder-scoped Raise Query. Who sees the
  // "+Query" affordance is the RBAC grid's queries.create (23 Oct 2026);
  // QUERY_RAISE_ROLES only covers the moment before permissions load.
  const canRaiseQuery = useMyPermissions().holdsOperation('queries.create', QUERY_RAISE_ROLES);
  const [queryFolder, setQueryFolder] = useState<BinderFolderRow | null>(null);
  const binderStudyId = q.data?.binder?.study_id ?? '';

  useEffect(() => {
    if (selectedFolderId) return;
    const first = sortedFolders[0];
    if (!first) return;
    setSelectedFolderId(first.folder_id);
  }, [sortedFolders, selectedFolderId]);

  // Deep link from the eSignature Queue, the dashboard Approvals card and
  // the approval invitation email: ?folder=<id>&document=<id>&version=<id>
  // selects the folder and opens the document at that version, where the
  // approver signs. Runs after the effect above so the folder wins over the
  // first-folder default; the params are then dropped so closing the viewer
  // and refreshing doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const docId = searchParams.get('document');
    if (!docId || !q.data) return;
    const folderId = searchParams.get('folder');
    if (folderId && folders.some((f) => f.folder_id === folderId)) setSelectedFolderId(folderId);
    setViewerDocId(docId);
    setViewerVersionId(searchParams.get('version'));
    setSearchParams((prev) => {
      prev.delete('document');
      prev.delete('folder');
      prev.delete('version');
      return prev;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, q.data]);

  const selectedFolder = useMemo(
    () => folders.find((f) => f.folder_id === selectedFolderId) ?? null,
    [folders, selectedFolderId],
  );

  // Build breadcrumb from the selected folder's zone + section + folder name.
  const breadcrumb = useMemo(() => {
    if (!selectedFolder) return ['All Documents'];
    return [
      'All Documents',
      selectedFolder.zone_name,
      selectedFolder.section_name,
      folderDisplayName(selectedFolder),
    ].filter((s, i, arr) => s && (i === 0 || s !== arr[i - 1]));
  }, [selectedFolder]);

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading binder…
      </div>
    );
  }

  if (q.isError || !q.data || !q.data.binder) {
    return (
      <div className="space-y-3 p-6">
        <div className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {q.error instanceof ApiError
              ? q.error.message
              : q.data && !q.data.binder
                ? 'Binder record missing from server response.'
                : 'Failed to load binder.'}
          </span>
        </div>
      </div>
    );
  }

  const { binder: row } = q.data;

  if (!row.reference_number) {
    return (
      <div className="space-y-3 p-6">
        <div className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Binder is missing its reference_number — re-run the Sprint-12 backfill.</span>
        </div>
      </div>
    );
  }

  return (
    /* Expand to fill the content area that AppShell provides. The AppShell
       already wraps main in `max-w-report p-6 md:p-8`; here we reach outside
       that padding with negative margin so the binder fills edge-to-edge. */
    <div className="-mx-6 -my-6 md:-mx-8 md:-my-8 flex h-[calc(100vh-60px)] overflow-hidden p-3">

      {/* ── Left sidebar: study selector + folder tree ─────────── */}
      <aside className="flex w-[260px] shrink-0 flex-col border-r border-border bg-surface overflow-hidden">

        {/* Study / binder selector */}
        <div className="border-b border-border-soft px-4 py-4">
          <div className="mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
              Study / Binder
            </span>
          </div>
          <div className="relative">
            <select
              value={binderRef}
              onChange={(e) => {
                navigate(`/binder/${encodeURIComponent(e.target.value)}`);
                setSelectedFolderId(null);
              }}
              className={cn(
                'w-full appearance-none rounded border border-border bg-white py-2 pl-3 pr-8',
                'text-[13px] font-semibold text-ink-primary outline-none',
                'focus:border-navy focus:ring-1 focus:ring-navy/20',
              )}
            >
              {allBinders.length === 0 && (
                <option value={binderRef}>
                  {binderOptionLabel(row)}
                </option>
              )}
              {allBinders.map((b) => {
                return (
                  <option key={b.binder_id} value={b.reference_number ?? b.binder_id}>
                    {binderOptionLabel(b)}
                  </option>
                );
              })}
            </select>
            <ChevronDown
              size={14}
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
          </div>
          {/* Show reference number as subtitle */}
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-ink-muted font-mono">
            {binderRef}
            <BlindingScopeBadge scope={binderScope(row)} />
          </div>
        </div>

        {!binderBlinded && folders.length > 0 && (
          <div className="border-b border-border-soft px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
              <input
                type="search"
                value={treeSearch}
                onChange={(e) => setTreeSearch(e.target.value)}
                placeholder="Search folders…"
                aria-label="Search folders"
                className="input w-full pl-7 text-xs"
              />
            </div>
          </div>
        )}

        {/* Folder tree */}
        <div className="flex-1 overflow-y-auto py-1">
          {binderBlinded ? (
            <div className="p-3">
              <BlindedNotice contentLabel="This binder" roleMasterRef={binderBlindedRoleRef} />
            </div>
          ) : folders.length === 0 ? (
            <div className="p-4 text-center text-xs text-ink-muted">
              No folders provisioned.
            </div>
          ) : (
            <BinderFolderTree
              folders={folders}
              selectedFolderId={selectedFolderId}
              completeness={folderCompleteness}
              filter={treeSearch}
              onSelect={(id) => {
                setSelectedFolderId(id);
                setViewerDocId(null);
                setViewerVersionId(null);
              }}
              onRaiseQuery={
                canRaiseQuery && binderStudyId
                  ? (folder) => setQueryFolder(folder)
                  : undefined
              }
            />
          )}
        </div>
      </aside>

      {/* ── Main content area ──────────────────────────────────── */}
      <div className="flex flex-1 flex-col min-w-0 bg-white overflow-hidden">

        {/* Top toolbar */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-3 shrink-0">
          {/* Breadcrumb */}
          <nav className="flex min-w-0 items-center gap-1 text-[13px] text-ink-muted overflow-hidden">
            {breadcrumb.map((seg, i) => (
              <span key={i} className="flex items-center gap-1 min-w-0">
                {i > 0 && <ChevronRight size={12} className="shrink-0 opacity-50" />}
                <span
                  className={cn(
                    'truncate',
                    i === breadcrumb.length - 1
                      ? 'font-semibold text-ink-primary'
                      : 'hover:text-ink-primary cursor-default',
                  )}
                >
                  {seg}
                </span>
              </span>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setExternalOpen((v) => !v)}
              className="btn-ghost text-xs"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              External access
            </button>
            <Link
              to={`/binder/${encodeURIComponent(binderRef)}/doa/grid`}
              className="btn-ghost text-xs"
            >
              <ClipboardSignature className="h-3.5 w-3.5" />
              DoA Grid
            </Link>
            {can('bulk_export_binder') && (
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                className="btn-ghost text-xs"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </button>
            )}
          </div>
        </div>

        {/* External grants panel */}
        {externalOpen && (
          <div className="border-b border-border shrink-0">
            <ExternalGrantsTab binderId={binderId} studyId={binderStudyId || undefined} />
          </div>
        )}

        {/* Document list */}
        <div className="flex-1 overflow-y-auto px-8 py-8 pb-12">
          {!selectedFolder ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-ink-muted">
              <FileText className="h-8 w-8 opacity-30" />
              <p>Select a folder from the left sidebar.</p>
            </div>
          ) : (
            <DocumentSlotList
              binderId={binderId}
              folderId={selectedFolder.folder_id}
              folderLabel={folderDisplayName(selectedFolder)}
              folderLevel={selectedFolder.folder_level}
              onOpenViewer={(id, vid) => { setViewerDocId(id); setViewerVersionId(vid ?? null); }}
            />
          )}
        </div>
      </div>

      <DocumentViewerModal
        binderRef={binderRef}
        documentRef={viewerDocId ?? ''}
        versionId={viewerVersionId ?? undefined}
        studyId={binderStudyId || undefined}
        open={!!viewerDocId}
        onClose={() => {
          setViewerDocId(null);
          setViewerVersionId(null);
          // The viewer's own status (sign/approve/archive) can change the
          // folder table's status/version columns — that query shares this
          // exact key, so refresh it on close instead of leaving it stale.
          qc.invalidateQueries({
            queryKey: ['binder', binderId, 'folder', selectedFolderId ?? '', 'documents'],
          });
        }}
      />

      <BulkExportDialog
        binderRef={binderRef}
        open={exportOpen}
        onClose={() => setExportOpen(false)}
      />

      {queryFolder && binderStudyId && (
        // Phase 4 base FE (AC 4.1) — folder-scoped Raise Query dialog.
        <RaiseFolderQueryDialog
          folder={queryFolder}
          studyId={binderStudyId}
          onClose={() => setQueryFolder(null)}
        />
      )}
    </div>
  );
}
